import React from 'react';
import { Platform, View, StyleSheet, ViewStyle } from 'react-native';

import { Text } from './text';

import { useTheme } from '@/components/theme-provider';

export interface CardProps {
	id?: string;
	className?: string;
	children: React.ReactNode;
	style?: any;
	elevation?: number;
	onPress?: () => void;
	testID?: string;
}

const CardBase: React.FC<CardProps> = ({ children, style, elevation = 1, onPress, className, testID }) => {
	const theme = useTheme();

	const cardStyle: ViewStyle = {
		backgroundColor: theme.colors.surface,
		borderRadius: 12,
		padding: 16,
		marginVertical: 4,
		...(elevation > 0 && {
			shadowColor: theme.colors.shadow,
			shadowOffset: { width: 0, height: elevation },
			shadowOpacity: 0.1,
			shadowRadius: elevation * 2,
			elevation: elevation,
		}),
	};

	return (
		<View
			testID={testID}
			style={[cardStyle, style]}
			onTouchEnd={onPress}
			{...(onPress ? { accessibilityRole: 'button' as const } : {})}
			{...(Platform.OS === 'web' ? { className } : {})}
		>
			{children}
		</View>
	);
};

// Card sub-components
export const CardTitle: React.FC<{ children?: React.ReactNode; title?: string; style?: ViewStyle }> = ({ children, title, style }) => (
	<View style={[styles.cardTitle, style]}>
		<Text variant="titleLarge">{title || children}</Text>
	</View>
);

export const CardContent: React.FC<{ children: React.ReactNode; style?: ViewStyle }> = ({ children, style }) => (
	<View style={[styles.cardContent, style]}>
		{children}
	</View>
);

export const CardActions: React.FC<{ children: React.ReactNode; style?: ViewStyle }> = ({ children, style }) => (
	<View style={[styles.cardActions, style]}>
		{children}
	</View>
);

// Create a properly typed Card component with sub-components
const Card = Object.assign(CardBase, {
	Title: CardTitle,
	Content: CardContent,
	Actions: CardActions,
});

export { Card };

const styles = StyleSheet.create({
	cardTitle: {
		marginBottom: 8,
	},
	cardContent: {
		marginBottom: 8,
	},
	cardActions: {
		flexDirection: 'row',
		justifyContent: 'flex-end',
		gap: 8,
	},
});
